import { useState, useEffect, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid, Car, Calendar, ClipboardList,
  Receipt, Repeat, Users, BarChart3, FileText, Wallet, CalendarX,
  Settings as SettingsIcon, LogOut, Crown, Menu, X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Avatar from './Avatar';

type IconType = typeof LayoutGrid;
type NavLeaf = { to: string; labelKey: string; icon: IconType };
type NavGroup = { labelKey: string; items: NavLeaf[] };
type NavEntry = NavLeaf | NavGroup;

const NAV: NavEntry[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutGrid },
  {
    labelKey: 'nav.fleet_group',
    items: [
      { to: '/vehicles', labelKey: 'nav.vehicles', icon: Car },
      { to: '/calendar', labelKey: 'nav.calendar', icon: Calendar },
      { to: '/bookings', labelKey: 'nav.bookings', icon: ClipboardList },
    ],
  },
  {
    labelKey: 'nav.finance_group',
    items: [
      { to: '/expenses',   labelKey: 'nav.expenses',   icon: Receipt },
      { to: '/recurring',  labelKey: 'nav.recurring',  icon: Repeat },
      { to: '/gomore',     labelKey: 'nav.gomore',     icon: Wallet },
      { to: '/cancellations', labelKey: 'nav.cancellations', icon: CalendarX },
    ],
  },
  {
    labelKey: 'nav.admin_group',
    items: [
      { to: '/renters',    labelKey: 'nav.renters',    icon: Users },
      { to: '/statistics', labelKey: 'nav.statistics', icon: BarChart3 },
      { to: '/reports',    labelKey: 'nav.reports',    icon: FileText },
    ],
  },
];

function isLeaf(e: NavEntry): e is NavLeaf {
  return 'to' in e;
}

function getInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
}

const navItemClass = () =>
  'flex items-center gap-2.5 px-3 py-2 rounded-[5px] text-[13.5px] font-medium tracking-tight transition-colors text-ink-700 hover:bg-sage-100 hover:text-ink-900';

const groupLabelClass =
  'text-sm font-bold uppercase tracking-[0.1em] text-sage-500 px-3 pt-4 pb-1.5';

function NavTree() {
  const { t } = useTranslation();
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
      {NAV.map((entry, i) => {
        if (isLeaf(entry)) {
          const Icon = entry.icon;
          return (
            <NavLink key={entry.to} to={entry.to} className={navItemClass}>
              <Icon size={16} className="text-ink-500" />
              {t(entry.labelKey)}
            </NavLink>
          );
        }
        return (
          <div key={`g-${i}`}>
            <div className={groupLabelClass}>{t(entry.labelKey)}</div>
            {entry.items.map(it => {
              const Icon = it.icon;
              return (
                <NavLink key={it.to} to={it.to} className={navItemClass}>
                  <Icon size={16} className="text-ink-500" />
                  {t(it.labelKey)}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}

function SidebarFooter() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="border-t border-ink-100 px-3 py-3 space-y-0.5">
      <NavLink to="/settings" className={navItemClass}>
        <SettingsIcon size={16} className="text-ink-500" />
        {t('nav.settings')}
      </NavLink>
      {user.isSuperadmin && (
        <a
          href="/hub"
          className="flex items-center gap-2.5 px-3 py-2 rounded-[5px] text-[13.5px] font-medium text-rose-700 hover:bg-sage-100 transition-colors"
        >
          <Crown size={16} />
          Hub
        </a>
      )}
      <button
        type="button"
        onClick={logout}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-[5px] text-[13.5px] font-medium text-ink-500 hover:bg-sage-100 hover:text-ink-900 transition-colors"
      >
        <LogOut size={16} />
        Log ud
      </button>

      <div className="flex items-center gap-3 px-3 pt-3 mt-2 border-t border-ink-100">
        <Avatar initials={getInitials(user.name)} size="md" variant="sage" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-900 truncate">{user.name}</div>
          <div className="text-xs text-ink-500 truncate">{user.email}</div>
        </div>
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div className="px-5 py-5 border-b border-ink-100">
      <div className="font-bold text-lg leading-none tracking-tight" style={{ letterSpacing: '-0.025em' }}>
        <span style={{ color: '#131916' }}>RentalFleet</span>
        <span className="text-sage-700">OS</span>
      </div>
      <div className="text-[11px] text-ink-500 mt-1.5">579 ApS</div>
    </div>
  );
}

export default function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-cream-100 text-ink-900">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-cream-50/90 backdrop-blur border-b border-ink-100">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setMobileOpen(o => !o)}
            className="p-2 -ml-1 rounded-lg text-ink-700 hover:bg-cream-100"
            aria-label={mobileOpen ? 'Luk menu' : 'Åbn menu'}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="font-bold text-sm" style={{ letterSpacing: '-0.025em' }}>
            <span style={{ color: '#131916' }}>RentalFleet</span>
            <span className="text-sage-700">OS</span>
          </span>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[49px] z-40 bg-[#ece9e2]">
          <div className="flex flex-col h-full">
            <NavTree />
            <SidebarFooter />
          </div>
        </div>
      )}

      <div className="md:grid md:grid-cols-[240px_1fr]">
        {/* Desktop sidebar — sticky */}
        <aside className="hidden md:flex md:flex-col bg-[#ece9e2] border-r border-ink-100 sticky top-0 h-screen">
          <Brand />
          <NavTree />
          <SidebarFooter />
        </aside>

        {/* Page content slot — pages render their own <main> with padding */}
        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
}
